import React from "react";

export type RushaMarkVariant = "default" | "glow" | "nodes" | "orbit";

export interface RushaMarkProps {
  size?: number;
  variant?: RushaMarkVariant;
  withWordmark?: boolean;
  "data-testid"?: string;
}

/**
 * Rusha logo mark — "Converge Mark" Concept 3 (Minimalist Forward R).
 *
 * Constructed from 3 gradient strokes (stem, bowl, diagonal leg) with
 * negative-space cutouts via SVG mask, and a control-plane node dot at
 * the bowl/leg junction. Variants add glow, connection nodes, or
 * orbital ring with orbiting nodes.
 */
export const RushaMark: React.FC<RushaMarkProps> = ({
  size = 20,
  variant = "default",
  withWordmark = false,
  "data-testid": testId = "rusha-mark",
}) => {
  const gradId = `rushaGrad-${variant}-${size}`;
  const glowId = `rushaGlow-${variant}-${size}`;
  const maskId = `rushaMask-${variant}-${size}`;

  const showGlow = variant === "glow" || variant === "orbit";
  const showNodes = variant === "nodes";
  const showOrbit = variant === "orbit";

  // Mask for negative-space cutout (creates letterform openings)
  const cutout = (
    <mask id={maskId}>
      <rect width="120" height="120" fill="white" />
      <path
        d="M 32 28 L 75 28 C 88 28 93 38 93 48 C 93 56 86 62 75 62 L 32 62 Z"
        fill="black"
      />
      <polygon points="58,65 98,102 88,108 52,72" fill="black" />
    </mask>
  );

  const strokes = (
    <g mask={`url(#${maskId})`}>
      <path
        d="M 22 18 L 22 102"
        stroke={`url(#${gradId})`}
        strokeWidth="12"
        strokeLinecap="round"
        fill="none"
        filter={showGlow ? `url(#${glowId})` : undefined}
      />
      <path
        d="M 22 18 L 78 18 C 98 18 105 35 105 48 C 105 62 95 72 78 72 L 22 72"
        stroke={`url(#${gradId})`}
        strokeWidth="12"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
        filter={showGlow ? `url(#${glowId})` : undefined}
      />
      <path
        d="M 58 65 L 98 102"
        stroke={`url(#${gradId})`}
        strokeWidth="12"
        strokeLinecap="round"
        fill="none"
        filter={showGlow ? `url(#${glowId})` : undefined}
      />
    </g>
  );

  const node = (
    <>
      <circle
        cx="60"
        cy="60"
        r="6"
        fill="#00d4ff"
        filter={showGlow ? `url(#${glowId})` : undefined}
      />
      <circle cx="60" cy="60" r="2.5" fill="#ffffff" />
    </>
  );

  const connectionNodes = showNodes && (
    <>
      <circle cx="22" cy="18" r="3.5" fill="#00d4ff" opacity="0.7" />
      <circle cx="22" cy="102" r="3.5" fill="#a855f7" opacity="0.7" />
      <circle cx="98" cy="102" r="3.5" fill="#00d4ff" opacity="0.7" />
    </>
  );

  const orbitElements = showOrbit && (
    <>
      <circle
        cx="60"
        cy="60"
        r="55"
        fill="none"
        stroke={`url(#${gradId})`}
        strokeWidth="0.5"
        strokeOpacity="0.15"
      />
      <circle
        cx="60"
        cy="5"
        r="3"
        fill="none"
        stroke="#00d4ff"
        strokeWidth="1.5"
        strokeOpacity="0.3"
      />
      <circle cx="60" cy="5" r="1.2" fill="#00d4ff" opacity="0.5" />
      <circle
        cx="108"
        cy="85"
        r="3"
        fill="none"
        stroke="#a855f7"
        strokeWidth="1.5"
        strokeOpacity="0.3"
      />
      <circle cx="108" cy="85" r="1.2" fill="#a855f7" opacity="0.5" />
      <circle
        cx="12"
        cy="85"
        r="3"
        fill="none"
        stroke="#22c55e"
        strokeWidth="1.5"
        strokeOpacity="0.3"
      />
      <circle cx="12" cy="85" r="1.2" fill="#22c55e" opacity="0.5" />
    </>
  );

  const svg = (
    <svg
      width={size}
      height={size}
      viewBox="0 0 120 120"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      data-testid={testId}
      role="img"
      aria-label="Rusha logo"
    >
      <defs>
        <linearGradient id={gradId} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#00d4ff" />
          <stop offset="100%" stopColor="#a855f7" />
        </linearGradient>
        {showGlow && (
          <filter id={glowId} x="-30%" y="-30%" width="160%" height="160%">
            <feGaussianBlur stdDeviation="3" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        )}
        {cutout}
      </defs>
      {strokes}
      {node}
      {connectionNodes}
      {orbitElements}
    </svg>
  );

  if (!withWordmark) return svg;

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
      }}
    >
      {svg}
      <span
        style={{
          fontFamily: "Inter, -apple-system, system-ui, sans-serif",
          fontWeight: 700,
          fontSize: size * 0.9,
          letterSpacing: "-0.01em",
          color: "inherit",
        }}
      >
        Rusha
      </span>
    </span>
  );
};

RushaMark.displayName = "RushaMark";
